import {
  Alert,
  Badge,
  Button,
  CloseButton,
  createStyles,
  Divider,
  FileButton,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useRef} from 'react';

import EventTriggerConfigForm from './EventTriggerConfigForm';
import RoutineFieldHeader from './RoutineFieldHeader';
import {canSaveRoutine} from './routineFormValidation';
import {
  AllowedEventTrigger,
  EventTriggerConfig,
  RoutineStatus,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';
import TimeTriggerConfigForm from './TimeTriggerConfigForm';
import TriggerTypeSelector from './TriggerTypeSelector';
import {RoutineFormState} from './useRoutineForm';

interface RoutineFormProps {
  isEdit: boolean;
  formState: RoutineFormState;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPromptChange: (value: string) => void;
  onRemoveFileBlock: (index: number) => void;
  onAddFile: (file: File) => void;
  isUploading: boolean;
  onTriggerTypeChange: (value: TriggerType) => void;
  onTriggerConfigChange: (updates: Partial<TimeTriggerConfig | EventTriggerConfig>) => void;
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onArchive?: () => void;
  currentStatus?: RoutineStatus;
  allowedEventTriggers: Array<AllowedEventTrigger>;
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
}

const useStyles = createStyles((theme) => ({
  form: {
    overflowY: 'auto',
    height: '100%',
  },
}));

export default function RoutineForm({
  isEdit,
  formState,
  onNameChange,
  onDescriptionChange,
  onPromptChange,
  onRemoveFileBlock,
  onAddFile,
  isUploading,
  onTriggerTypeChange,
  onTriggerConfigChange,
  isDirty,
  isSaving,
  error,
  onSave,
  onCancel,
  onPause,
  onResume,
  onArchive,
  currentStatus,
  allowedEventTriggers,
  timeBasedEnabled,
  eventBasedEnabled,
}: RoutineFormProps): JSX.Element {
  const {classes} = useStyles();
  const [archiveModalOpened, {open: openArchiveModal, close: closeArchiveModal}] = useDisclosure(false);
  const resetFileInputRef = useRef<() => void>(null);

  const isTimeBased = formState.triggerType === TriggerType.TIME_BASED;

  const canSave = canSaveRoutine(formState, isEdit, isDirty, isSaving, isUploading);

  return (
    <div className={classes.form}>
      <Stack spacing="md">
        <Stack spacing={4}>
          <Title order={4}>{isEdit ? 'Edit Background Agent' : 'Create new Background Agent'}</Title>
          <Text size="md" c="dimmed">
            When this Background Agent runs, it starts a new chat in your chat list — just like starting a
            chat yourself. You will also receive an email notification when it's complete if you have email
            notifications on.
          </Text>
        </Stack>

        <TextInput
          label="Name"
          value={formState.name}
          onChange={(e) => {
            onNameChange(e.currentTarget.value);
          }}
        />

        <Textarea
          label="Description"
          description="A summary of what this Background Agent does - this will not be sent along to the agent."
          placeholder="optional"
          minRows={2}
          value={formState.description}
          onChange={(e) => {
            onDescriptionChange(e.currentTarget.value);
          }}
        />

        <Textarea
          label="Instructions"
          description="The prompt that will be sent to the agent when this Background Agent runs."
          minRows={4}
          value={formState.prompt}
          onChange={(e) => {
            onPromptChange(e.currentTarget.value);
          }}
        />

        <Stack spacing="xs">
          <RoutineFieldHeader
            title="Attachments"
            description="Attach files to provide additional context to the agent when this Background Agent runs."
          />
          {formState.fileBlocks.length > 0 && (
            <Group spacing="xs">
              {formState.fileBlocks.map((fileBlock, index) => (
                <Badge
                  key={`${fileBlock.external_file_id ?? fileBlock.s3_location}-${index}`}
                  variant="outline"
                  size="sm"
                  rightSection={
                    <CloseButton
                      aria-label={`Remove ${fileBlock.filename}`}
                      size="xs"
                      onClick={() => {
                        onRemoveFileBlock(index);
                      }}
                    />
                  }
                >
                  {fileBlock.filename}
                </Badge>
              ))}
            </Group>
          )}
          <Group>
            <FileButton
              resetRef={resetFileInputRef}
              onChange={(file) => {
                if (file != null) {
                  onAddFile(file);
                }
                // Reset so selecting the same file again re-triggers onChange.
                resetFileInputRef.current?.();
              }}
            >
              {(props) => (
                <Button {...props} variant="secondary" size="sm" disabled={isUploading} loading={isUploading}>
                  + Add file
                </Button>
              )}
            </FileButton>
          </Group>
        </Stack>

        <Divider />

        <TriggerTypeSelector
          value={formState.triggerType}
          onChange={onTriggerTypeChange}
          timeBasedEnabled={timeBasedEnabled}
          eventBasedEnabled={eventBasedEnabled}
          disabled={isEdit}
        />

        {formState.triggerType != null &&
          (isTimeBased ? (
            <TimeTriggerConfigForm
              config={formState.triggerConfig as Partial<TimeTriggerConfig>}
              onChange={onTriggerConfigChange}
            />
          ) : (
            <EventTriggerConfigForm
              config={formState.triggerConfig as Partial<EventTriggerConfig>}
              onChange={onTriggerConfigChange}
              allowedTriggers={allowedEventTriggers}
            />
          ))}

        {error != null && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group position="apart" pt="md">
          <Group spacing="xs">
            {isEdit && onArchive && (
              <Button variant="warn" size="sm" onClick={openArchiveModal} disabled={isSaving}>
                Archive
              </Button>
            )}
            {isEdit && currentStatus === RoutineStatus.ACTIVE && onPause && (
              <Button variant="secondary" size="sm" onClick={onPause} disabled={isSaving}>
                Pause
              </Button>
            )}
            {isEdit && currentStatus === RoutineStatus.PAUSED && onResume && (
              <Button variant="secondary" size="sm" onClick={onResume} disabled={isSaving}>
                Resume
              </Button>
            )}
          </Group>
          <Group spacing="xs">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={onSave} disabled={!canSave} loading={isSaving}>
              Save
            </Button>
          </Group>
        </Group>
        <Modal
          opened={archiveModalOpened}
          onClose={closeArchiveModal}
          title="Archive Background Agent"
          centered={true}
          closeOnClickOutside={true}
        >
          <Stack spacing="md">
            <Text size="sm">Are you sure? This will stop and remove this Background Agent.</Text>
            <Group position="right" spacing="xs">
              <Button variant="ghost" size="sm" onClick={closeArchiveModal}>
                Cancel
              </Button>
              <Button
                variant="warn"
                size="sm"
                onClick={() => {
                  closeArchiveModal();
                  onArchive?.();
                }}
              >
                Archive
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </div>
  );
}
